import React from 'react';
import {
	Button,
	Checkbox,
	CircularProgress,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Toolbar,
} from 'components/mui';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { AddIcon, CopyIcon, DeleteIcon, ReorderIcon } from 'components/icons';
import styles from './CheckpointTable.module.css';
import useCheckpointTable from 'containers/pages/checklists/hooks/useCheckpointTable';

const TableBodyWithOneRow = ({ children }) => {
	return (
		<TableBody>
			<TableRow>
				<TableCell
					colSpan={12}
					className={styles.wrap_loading_indicator}
				>
					{children}
				</TableCell>
			</TableRow>
		</TableBody>
	);
};

const EnhancedTableToolbar = ({
	onNewCheckpointClick,
	onDeleteCheckpointClick,
	onCopyCheckpointClick,
	pending,
}) => {
	return (
		<Toolbar className={styles.toolBar}>
			<Button
				variant="contained"
				color="sds"
				startIcon={<AddIcon />}
				onClick={() => onNewCheckpointClick(null)}
				disabled={pending}
			>
				CHECKPOINT
			</Button>
			<div>
				<IconButton
					onClick={() => onCopyCheckpointClick()}
					disabled={pending}
				>
					<CopyIcon />
				</IconButton>
				<IconButton
					onClick={() => onDeleteCheckpointClick()}
					disabled={pending}
				>
					<DeleteIcon />
				</IconButton>
			</div>
		</Toolbar>
	);
};
const CheckpointTable = ({
	checkpoints,
	setCheckpoints,
	checklist,
	highlightedCheckpointID,
	setHighlightedCheckpointID,
	displayCheckpointTableMessage,
	inputValidationHelpers,
	pending,
}) => {
	const state = useCheckpointTable(
		checkpoints,
		setCheckpoints,
		checklist,
		highlightedCheckpointID,
		setHighlightedCheckpointID,
		inputValidationHelpers,
	);

	return (
		<>
			<EnhancedTableToolbar
				onNewCheckpointClick={state.onNewCheckpointClick}
				onDeleteCheckpointClick={state.onDeleteCheckpointClick}
				onCopyCheckpointClick={state.onCopyCheckpointClick}
				pending={state.pending}
			/>
			<TableContainer
				className={styles.tableContainer}
				ref={state.tableContainerRef}
			>
				<Table size={'small'} stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell
								align="left"
								className={styles.checkbox_col}
							/>
							<TableCell
								align="left"
								className={styles.checkbox_col}
							>
								<Checkbox
									disabled={pending}
									color="primary"
									checked={state.selectAll}
									onChange={state.onSelectAllCheckpoints}
								/>
							</TableCell>
							<TableCell align="left">Checkpoint </TableCell>
						</TableRow>
					</TableHead>

					{pending && (
						<TableBodyWithOneRow>
							<CircularProgress />
						</TableBodyWithOneRow>
					)}
					{!pending && checkpoints.length === 0 && (
						<TableBodyWithOneRow>
							{displayCheckpointTableMessage()}
						</TableBodyWithOneRow>
					)}
					{!pending && checkpoints.length > 0 && (
						<TableBodyWithDragNDrop
							checkpoints={checkpoints}
							handleDragEnd={state.handleDragEnd}
							highlightedCheckpointID={highlightedCheckpointID}
							onClickCheckpoint={state.onClickCheckpoint}
							onSelectCheckpointID={state.onSelectCheckpointID}
						/>
					)}
				</Table>
			</TableContainer>
		</>
	);
};
const TableBodyWithDragNDrop = ({
	checkpoints,
	handleDragEnd,
	highlightedCheckpointID,
	onClickCheckpoint,
	onSelectCheckpointID,
}) => {
	return (
		<DragDropContext onDragEnd={handleDragEnd}>
			<Droppable droppableId="droppable" direction="vertical">
				{(droppableProvided) => (
					<TableBody
						ref={droppableProvided.innerRef}
						{...droppableProvided.droppableProps}
					>
						{checkpoints &&
							checkpoints.map((checkpoint, index) => (
								<Draggable
									key={checkpoint.CheckpointID}
									draggableId={checkpoint.CheckpointID + ''} //draggableId needs to be a string...
									index={index}
								>
									{(draggableProvided) => {
										return (
											<TableRow
												ref={draggableProvided.innerRef}
												{...draggableProvided.draggableProps}
												key={checkpoint.CheckpointID}
												className={styles.tableRow}
												selected={
													checkpoint.CheckpointID ===
													highlightedCheckpointID
												}
											>
												<TableCell
													align="left"
													className={
														styles.checkbox_col
													}
												>
													<div
														{...draggableProvided.dragHandleProps}
													>
														<ReorderIcon />
													</div>
												</TableCell>
												<TableCell
													align="left"
													className={
														styles.checkbox_col
													}
												>
													<Checkbox
														checked={
															checkpoint.isSelected ||
															false
														}
														onChange={() => {
															onSelectCheckpointID(
																checkpoint.CheckpointID,
															);
														}}
													/>
												</TableCell>
												<TableCell
													align="left"
													component="th"
													scope="row"
													onClick={() => {
														onClickCheckpoint(
															checkpoint,
														);
													}}
												>
													{checkpoint.Name}
												</TableCell>
											</TableRow>
										);
									}}
								</Draggable>
							))}
						{droppableProvided.placeholder}
					</TableBody>
				)}
			</Droppable>
		</DragDropContext>
	);
};

export default CheckpointTable;
